// Utility for parsing voice transcripts into structured fields during onboarding

export const WORDS_TO_DIGITS = {
  zero: '0', one: '1', two: '2', three: '3', four: '4', five: '5', six: '6',
  seven: '7', eight: '8', nine: '9'
};

const WORD_VALUES = {
  zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9,
  ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16,
  seventeen: 17, eighteen: 18, nineteen: 19, twenty: 20, thirty: 30, forty: 40,
  fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90, hundred: 100, thousand: 1000
};

export const wordsToNumber = (str) => {
  const words = str.toLowerCase().match(/[a-z]+/g) || [];
  let total = 0;
  let current = 0;
  let matchedAny = false;

  for (const word of words) {
    if (WORD_VALUES[word] !== undefined) {
      matchedAny = true;
      const val = WORD_VALUES[word];
      if (val === 100) {
        current = (current || 1) * 100;
      } else if (val === 1000) {
        total += (current || 1) * 1000;
        current = 0;
      } else {
        current += val;
      }
    }
  }
  total += current;
  return matchedAny ? String(total) : null;
};

/**
 * Parses raw voice transcript string into sanitized field format based on field type
 * @param {object} question - Question metadata containing key/field/label
 * @param {string} rawAnswer - The raw transcription from Web Speech API
 * @returns {string} - Sanitized string suitable for formData
 */
export const parseVoiceAnswer = (question, rawAnswer) => {
  if (!rawAnswer) return '';
  const trimmed = String(rawAnswer).trim();
  if (!question) return trimmed;

  const fieldName = (question.field || question.key || '').toLowerCase();
  const isPincode = fieldName.includes('pincode') || fieldName.includes('pin');
  const isPhone = fieldName.includes('phone') || fieldName.includes('mobile') || fieldName.includes('contact');
  const isNumeric =
    isPincode ||
    isPhone ||
    fieldName.includes('bed') ||
    fieldName.includes('call') ||
    fieldName.includes('percent') ||
    fieldName.includes('pct') ||
    fieldName.includes('rate') ||
    fieldName.includes('km') ||
    fieldName.includes('fare') ||
    fieldName.includes('owned') ||
    fieldName.includes('attached') ||
    fieldName.includes('available') ||
    fieldName.includes('year') ||
    fieldName.includes('radius') ||
    fieldName.includes('trips');

  if (isPhone) {
    let cleaned = trimmed.toLowerCase();
    Object.entries(WORDS_TO_DIGITS).forEach(([word, num]) => {
      cleaned = cleaned.replace(new RegExp(`\\b${word}\\b`, 'g'), num);
    });
    const digitsOnly = cleaned.replace(/\D/g, '');
    if (digitsOnly.length === 12 && digitsOnly.startsWith('91')) {
      return digitsOnly.slice(2);
    }
    return digitsOnly || trimmed;
  }

  if (isPincode) {
    let cleaned = trimmed.toLowerCase();
    Object.entries(WORDS_TO_DIGITS).forEach(([word, num]) => {
      cleaned = cleaned.replace(new RegExp(`\\b${word}\\b`, 'g'), num);
    });
    const digitsOnly = cleaned.replace(/\D/g, '');
    if (digitsOnly.length >= 6) {
      return digitsOnly.slice(0, 6);
    }
    return digitsOnly || trimmed;
  }

  if (isNumeric) {
    // If digits exist in string (e.g. "150 calls" or "50%"), extract first number
    const numMatch = trimmed.match(/[-+]?\d*\.?\d+/);
    if (numMatch) {
      return numMatch[0];
    }
    // Else check if spoken words form a number (e.g. "twenty five", "one hundred")
    const parsedNumber = wordsToNumber(trimmed);
    if (parsedNumber !== null) {
      return parsedNumber;
    }
  }

  return trimmed;
};

export const isSpeechRecognitionSupported = () => {
  return typeof window !== 'undefined' && Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
};
